import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useBalance } from '../hooks/useBalance';
import { useUserData } from '../contexts/UserDataContext';
import { LeverageModal } from './LeverageModal';
import { MarginModeModal } from './MarginModeModal';
import type { ExecutionRoute, Hip3Market, SmartRoute } from '../types';

interface OrderEntryProps {
  selectedMarket: Hip3Market | null;
  onSubmitOrder: (side: 'buy' | 'sell', size: number, type: 'market' | 'limit', price?: number, leverage?: number) => void;
  onSizeChange?: (size: number, side: 'buy' | 'sell') => void;
  bestRoute: ExecutionRoute | null;
  smartRoute: SmartRoute | null;
  isLoading: boolean;
  isLoadingRoute?: boolean;
  currentPrice?: number;
}

export function OrderEntry({ selectedMarket, onSubmitOrder, onSizeChange, bestRoute: _bestRoute, smartRoute, isLoading, isLoadingRoute, currentPrice }: OrderEntryProps) {
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [size, setSize] = useState('');
  const [sizePercent, setSizePercent] = useState(0);
  const [price, setPrice] = useState('');
  const [leverage, setLeverage] = useState(20);
  const [marginMode, setMarginMode] = useState<'cross' | 'isolated'>('cross');
  const [maxSlippage, setMaxSlippage] = useState(0.5);
  const [showSlippageModal, setShowSlippageModal] = useState(false);
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [showMarginModeModal, setShowMarginModeModal] = useState(false);
  const [tempMaxSlippage, setTempMaxSlippage] = useState('0.5');
  const [reduceOnly, setReduceOnly] = useState(false);
  const [showTpSl, setShowTpSl] = useState(false);
  const { balance, isConnected } = useBalance();
  const { positions } = useUserData();

  const maxLeverage = selectedMarket?.maxLeverage || 50;

  // Get current position for selected market
  const currentPosition = positions.find(p => {
    const positionCoin = p.coin.toLowerCase();
    const marketAsset = selectedMarket?.baseAsset?.toLowerCase() || '';
    return positionCoin === marketAsset || positionCoin.includes(marketAsset);
  });

  const positionSize = currentPosition ? parseFloat(currentPosition.szi) : 0;
  const isLong = positionSize > 0;

  // Trigger smart route recalculation when size or side changes
  useEffect(() => {
    const sizeNum = parseFloat(size);
    if (onSizeChange && !isNaN(sizeNum) && sizeNum > 0) {
      onSizeChange(sizeNum, side);
    }
  }, [size, side, onSizeChange]);

  // Calculate max size based on balance and leverage
  const maxSize = currentPrice && currentPrice > 0 && isConnected
    ? (balance * leverage) / currentPrice
    : 0;

  // Update size when slider changes - direct calculation, no side effects
  const handleSliderChange = (percent: number) => {
    setSizePercent(percent);
    if (maxSize > 0) {
      const newSize = (maxSize * percent) / 100;
      setSize(newSize > 0 ? newSize.toFixed(4) : '');
    }
  };

  // Handle size input changes - update slider directly
  const handleSizeChange = (val: string) => {
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setSize(val);
      // Update slider percent directly
      const sizeNum = parseFloat(val);
      if (!isNaN(sizeNum) && maxSize > 0) {
        setSizePercent(Math.min(100, Math.max(0, (sizeNum / maxSize) * 100)));
      } else {
        setSizePercent(0);
      }
    }
  };

  const handleSubmit = () => {
    const sizeNum = parseFloat(size);
    if (isNaN(sizeNum) || sizeNum <= 0) {
      toast.error('Please enter a valid size');
      return;
    }

    if (orderType === 'limit') {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        toast.error('Please enter a valid price');
        return;
      }
      onSubmitOrder(side, sizeNum, orderType, priceNum, leverage);
    } else {
      onSubmitOrder(side, sizeNum, orderType, undefined, leverage);
    }
  };

  // Slider dot positions
  const sliderDots = [0, 25, 50, 75, 100];

  return (
    <div className="bg-[#0e0f10] border border-[#2d3748] overflow-hidden flex flex-col h-full">
      {/* Top Row - Cross / Leverage Buttons */}
      <div className="px-3 pt-3 pb-2 flex gap-2">
        <button
          onClick={() => setShowMarginModeModal(true)}
          className="flex-1 py-1.5 px-3 rounded text-[12px] font-medium bg-[#1a1d20] text-white border border-[#2d3748] hover:border-[#3d444d] transition-all"
        >
          {marginMode === 'cross' ? 'Cross' : 'Isolated'}
        </button>
        <button
          onClick={() => setShowLeverageModal(true)}
          className="flex-1 py-1.5 px-3 rounded text-[12px] font-medium bg-[#1a1d20] text-white border border-[#2d3748] hover:border-[#3d444d] transition-all"
        >
          {leverage}x
        </button>
      </div>

      {/* Order Type Tabs */}
      <div className="px-3 flex border-b border-[#2d3748]">
        <button
          onClick={() => setOrderType('market')}
          className={`flex-1 py-2 text-[12px] font-medium transition-all ${
            orderType === 'market'
              ? 'text-white border-b-2 border-[#3fb68b] -mb-[1px]'
              : 'text-[#6c7884] hover:text-[#b0b8c1]'
          }`}
        >
          Market
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className={`flex-1 py-2 text-[12px] font-medium transition-all ${
            orderType === 'limit'
              ? 'text-white border-b-2 border-[#3fb68b] -mb-[1px]'
              : 'text-[#6c7884] hover:text-[#b0b8c1]'
          }`}
        >
          Limit
        </button>
      </div>

      <div className="p-3 space-y-2.5 flex-1 flex flex-col min-h-0 overflow-y-auto">
        {/* Buy/Sell Toggle - Full Width Segmented */}
        <div className="flex rounded overflow-hidden border border-[#2d3748]">
          <button
            onClick={() => setSide('buy')}
            className={`flex-1 py-2 text-[12px] font-semibold transition-all ${
              side === 'buy'
                ? 'bg-[#3fb68b] text-white'
                : 'bg-[#0b0c0d] text-[#6c7884] hover:text-[#b0b8c1]'
            }`}
          >
            Buy / Long
          </button>
          <button
            onClick={() => setSide('sell')}
            className={`flex-1 py-2 text-[12px] font-semibold transition-all ${
              side === 'sell'
                ? 'bg-[#f6465d] text-white'
                : 'bg-[#0b0c0d] text-[#6c7884] hover:text-[#b0b8c1]'
            }`}
          >
            Sell / Short
          </button>
        </div>

        {/* Info Rows */}
        <div className="space-y-1">
          <div className="flex justify-between text-[12px]">
            <span className="text-[#6c7884]">Available to Trade</span>
            <span className="text-white">
              {isConnected ? `${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC` : '--'}
            </span>
          </div>
          <div className="flex justify-between text-[12px]">
            <span className="text-[#6c7884]">Current Position</span>
            <span className={`${positionSize !== 0 ? (isLong ? 'text-[#3fb68b]' : 'text-[#f6465d]') : 'text-white'}`}>
              {positionSize !== 0
                ? `${Math.abs(positionSize).toFixed(4)} ${selectedMarket?.baseAsset || ''}`
                : `0.0000 ${selectedMarket?.baseAsset || ''}`
              }
            </span>
          </div>
        </div>

        {/* Price Input - Only for limit orders */}
        {orderType === 'limit' && (
          <div className="relative">
            <input
              type="text"
              value={price}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                  setPrice(val);
                }
              }}
              placeholder="0.00"
              className="w-full pl-12 pr-14 py-2 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#3fb68b] transition-all"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
              Price
            </span>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
              USD
            </span>
          </div>
        )}

        {/* Size Input */}
        <div className="relative">
          <input
            type="text"
            value={size}
            onChange={(e) => handleSizeChange(e.target.value)}
            placeholder="0.00"
            className="w-full pl-10 py-2 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#3fb68b] transition-all"
            style={{ paddingRight: `${(selectedMarket?.baseAsset?.length || 3) * 8 + 20}px` }}
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
            Size
          </span>
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
            {selectedMarket?.baseAsset || 'USD'}
          </span>
        </div>

        {/* Size Slider with editable percentage - same row */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 h-1.5">
            {/* Track background */}
            <div className="absolute inset-0 bg-[#1f2937] rounded-full" />
            {/* Filled track */}
            <div
              className="absolute left-0 top-0 h-full bg-[#3fb68b] rounded-full"
              style={{ width: `${sizePercent}%` }}
            />
            {/* Dots */}
            <div className="absolute inset-0 flex justify-between items-center px-0">
              {sliderDots.map((dot) => (
                <button
                  key={dot}
                  onClick={() => handleSliderChange(dot)}
                  className={`w-2 h-2 rounded-full z-10 transition-transform hover:scale-125 ${
                    sizePercent >= dot ? 'bg-[#3fb68b]' : 'bg-[#3d444d]'
                  }`}
                />
              ))}
            </div>
            {/* Custom thumb that grows on active */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md cursor-grab active:cursor-grabbing active:scale-125 transition-transform z-20"
              style={{ left: `calc(${sizePercent}% - 6px)` }}
            />
            {/* Invisible range input for interaction */}
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={sizePercent}
              onChange={(e) => handleSliderChange(Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-30"
            />
          </div>
          {/* Editable percentage input - styled like Size input */}
          <div className="relative w-16">
            <input
              type="text"
              value={Math.round(sizePercent)}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                const num = parseInt(val) || 0;
                handleSliderChange(Math.min(100, Math.max(0, num)));
              }}
              className="w-full py-2 pr-8 pl-2 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:border-[#3fb68b] focus:outline-none"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">%</span>
          </div>
        </div>

        {/* Checkboxes - Custom styled */}
        <div className="space-y-1.5">
          <div
            onClick={() => setReduceOnly(!reduceOnly)}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div
              className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
                reduceOnly
                  ? 'bg-[#3fb68b] border-[#3fb68b]'
                  : 'bg-transparent border-[#3d444d] group-hover:border-[#6c7884]'
              }`}
            >
              {reduceOnly && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-[12px] text-[#b0b8c1] group-hover:text-white">Reduce Only</span>
          </div>
          <div
            onClick={() => setShowTpSl(!showTpSl)}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div
              className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
                showTpSl
                  ? 'bg-[#3fb68b] border-[#3fb68b]'
                  : 'bg-transparent border-[#3d444d] group-hover:border-[#6c7884]'
              }`}
            >
              {showTpSl && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-[12px] text-[#b0b8c1] group-hover:text-white">Take Profit / Stop Loss</span>
          </div>
        </div>

        {/* TP/SL Inputs - 2x2 Grid */}
        {showTpSl && (
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="0.00"
                className="w-full pl-16 pr-3 py-1.5 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#3fb68b] transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
                TP Price
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="0.00"
                className="w-full pl-12 pr-3 py-1.5 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#3fb68b] transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
                Gain
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="0.00"
                className="w-full pl-16 pr-3 py-1.5 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#f6465d] transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
                SL Price
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="0.00"
                className="w-full pl-12 pr-3 py-1.5 bg-[#0b0c0d] text-white text-[12px] text-right rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#f6465d] transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#6c7884]">
                Loss
              </span>
            </div>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1 min-h-[0.25rem]" />

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isLoading || !selectedMarket}
          className={`w-full py-2.5 rounded font-semibold text-[12px] transition-all ${
            side === 'buy'
              ? 'bg-[#3fb68b] hover:bg-[#36a07a] active:bg-[#2d8a69] text-white'
              : 'bg-[#f6465d] hover:bg-[#e03d52] active:bg-[#ca3647] text-white'
          } disabled:bg-[#1a1a1a] disabled:text-[#3d444d] disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Processing...' : 'Place Order'}
        </button>

        {/* Order Details */}
        <div className="space-y-1.5 text-[12px] pt-2 border-t border-[#1f2937]">
          {/* Avg Price with Smart Routing Tooltip */}
          <div className="group relative flex justify-between">
            <span className="text-[#6c7884] underline decoration-dotted underline-offset-2 cursor-help flex items-center gap-1">
              Avg Price
              {isLoadingRoute && (
                <span className="inline-block animate-spin rounded-full h-2.5 w-2.5 border-t border-b border-[#3fb68b]"></span>
              )}
            </span>
            <span className="text-white">
              {smartRoute ? `$${smartRoute.weightedAvgPrice.toFixed(2)}` : '--'}
            </span>
            {/* Smart Routing Tooltip */}
            {smartRoute && smartRoute.splits.length > 1 && (
              <div className="absolute bottom-full mb-2 left-0 w-56 bg-[#1a1a1a] border border-[#2d3748] rounded-md shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 text-[12px]">
                <div className="font-semibold text-white mb-2">
                  Order Routing
                </div>

                {/* Venue Distribution - Simple: Provider + Bar + % */}
                <div className="space-y-2">
                  {smartRoute.splits.map((split, idx) => {
                    const notional = split.size * split.expectedPrice;
                    const pct = smartRoute.totalNotional > 0 ? (notional / smartRoute.totalNotional) * 100 : 0;
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-white font-medium w-14 truncate">{split.provider}</span>
                        <div className="flex-1 h-1.5 bg-[#2d3748] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#3fb68b] rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[#3fb68b] w-10 text-right">{pct.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <span className="text-[#6c7884] underline decoration-dotted underline-offset-2">Liquidation Price</span>
            <span className="text-white">N/A</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6c7884]">Order Value</span>
            <span className="text-white">
              {currentPrice && size && parseFloat(size) > 0
                ? `${(parseFloat(size) * currentPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC`
                : 'N/A'
              }
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6c7884]">Margin Required</span>
            <span className="text-white">
              {currentPrice && size && parseFloat(size) > 0 && leverage > 1
                ? `${((parseFloat(size) * currentPrice) / leverage).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC`
                : 'N/A'
              }
            </span>
          </div>
          {orderType === 'market' && (
            <div className="flex justify-between">
              <span className="text-[#6c7884] underline decoration-dotted underline-offset-2">Slippage</span>
              <button
                onClick={() => {
                  setTempMaxSlippage(maxSlippage.toString());
                  setShowSlippageModal(true);
                }}
                className="text-[12px] hover:opacity-80 transition-opacity"
              >
                <span className="text-[#3fb68b]">Est: 0.04%</span>
                <span className="text-[#6c7884]"> / </span>
                <span className="text-white">Max: {maxSlippage.toFixed(2)}%</span>
              </button>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[#6c7884] underline decoration-dotted underline-offset-2">Fees</span>
            <span className="text-white">0.0350% / 0.0080%</span>
          </div>
        </div>
      </div>

      {/* Leverage Modal */}
      <LeverageModal
        isOpen={showLeverageModal}
        onClose={() => setShowLeverageModal(false)}
        currentLeverage={leverage}
        maxLeverage={maxLeverage}
        onConfirm={setLeverage}
      />

      {/* Margin Mode Modal */}
      <MarginModeModal
        isOpen={showMarginModeModal}
        onClose={() => setShowMarginModeModal(false)}
        currentMode={marginMode}
        onConfirm={setMarginMode}
      />

      {/* Slippage Modal */}
      {showSlippageModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowSlippageModal(false)}>
          <div className="bg-[#0e0f10] border border-[#2d3748] rounded-lg p-4 w-[320px] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-[13px]">Adjust Max Slippage</h3>
              <button
                onClick={() => setShowSlippageModal(false)}
                className="text-[#6c7884] hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-[#6c7884] text-[12px] mb-3 leading-relaxed">
              Slippage tolerance only applies to market orders. Your order will fail if the price moves unfavorably by more than this percentage.
            </p>
            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {[0.1, 0.5, 1.0, 2.0].map((preset) => (
                <button
                  key={preset}
                  onClick={() => setTempMaxSlippage(preset.toString())}
                  className={`py-1.5 px-2 rounded text-[12px] font-medium transition-all ${
                    parseFloat(tempMaxSlippage) === preset
                      ? 'bg-[#3fb68b] text-white'
                      : 'bg-[#0b0c0d] text-[#6c7884] border border-[#2d3748] hover:border-[#3d444d]'
                  }`}
                >
                  {preset}%
                </button>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-[10px] font-medium text-[#6c7884] mb-1">
                Custom (%)
              </label>
              <input
                type="number"
                value={tempMaxSlippage}
                onChange={(e) => setTempMaxSlippage(e.target.value)}
                step="0.01"
                min="0.01"
                max="50"
                className="w-full px-3 py-2 bg-[#0b0c0d] text-white text-[12px] rounded-lg border border-[#2d3748] focus:outline-none focus:border-[#3fb68b] transition-all"
              />
            </div>
            <button
              onClick={() => {
                const slippage = parseFloat(tempMaxSlippage);
                if (!isNaN(slippage) && slippage >= 0.01 && slippage <= 50) {
                  setMaxSlippage(slippage);
                  setShowSlippageModal(false);
                  toast.success(`Max slippage set to ${slippage.toFixed(2)}%`);
                } else {
                  toast.error('Please enter a valid slippage between 0.01% and 50%');
                }
              }}
              className="w-full py-2 bg-[#3fb68b] hover:bg-[#36a07a] text-white font-semibold text-[12px] rounded transition-all"
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
